/**
 * Harga jual per IMEI di layar kasir (brief owner 22 Sep 2026, G2 — jawaban
 * Bos no. 23 dipakai sebagai bawaan (a): "isi otomatis di nota, bisa diubah").
 *
 * SATU sumber aturan untuk Input Nota DAN Input Penjualan — dua salinan aturan
 * uang di dua halaman pasti berpisah jalan (jebakan `uang_diterima_nota`).
 *
 * KENAPA TANPA PENANDA "harga ini otomatis" DI BARIS
 * --------------------------------------------------
 * Penanda di item hilang saat draf dibuka ulang (FL "Kirim ke Kasir" → kasir
 * menyunting draf lalu memilih IMEI — alur utama Input Nota), dan menambahkan
 * penanda berarti menyentuh pemilih produk, input harga, dan pembentuk payload
 * yang sedang disunting batch lain. Maka "otomatis" DITURUNKAN dari data:
 * harga baris dianggap otomatis kalau sama dengan 0, harga produk, atau harga
 * per IMEI salah satu unit yang PERNAH muncul di respons pilihan-jual baris itu
 * sejak produknya dipilih (`hargaTerlihat`, dikumpulkan lintas respons). Satu
 * respons saja tidak cukup: respons CARI hanya memuat unit yang cocok ketikan,
 * jadi harga unit yang baru dilepas kasir tidak ada di sana — dan Input
 * Penjualan tidak punya daftar rekomendasi sama sekali. Himpunan itu dimulai
 * ulang saat SKU baris berganti (pemilih produk mengisi ulang harganya).
 * Harga dengan angka lain TIDAK PERNAH ditimpa — cukup pesan.
 *
 * Baris Bonus/Gratis tidak pernah diisi: backend menerima `is_bonus` dengan
 * harga > 0, jadi mengisi harga di sini = barang gratis ikut tertagih.
 *
 * Backend nota TIDAK membandingkan harga baris dengan harga per IMEI; semua
 * ini hanya bantuan pengisian. Kalau pengaturan Bos mematikan isi otomatis
 * (jawaban b/c), server tidak mengirim `harga_jual_unit` sama sekali dan
 * `infoHargaDariRespons().dipakai` bernilai false.
 */

const bulat = (n) => Math.round((Number(n) || 0) * 100) / 100;

const rupiah = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");

/** Satu penerima respons `GET /inventory/units/pilihan-jual` — dipakai SETIAP
 *  jalur muat daftar IMEI (rekomendasi per SKU dan pencarian ketik). */
export function infoHargaDariRespons(data) {
  const items = (data && data.items) || [];
  const dipakai = !!(data && data.harga_unit_dipakai);
  return {
    dipakai,
    hargaProduk: dipakai ? bulat(data.harga_produk) : 0,
    hargaUnit: dipakai
      ? items.map((u) => bulat(u.harga_jual_unit)).filter((h) => h > 0)
      : [],
  };
}

/** Info harga baris = respons TERBARU (bendera & harga produk) + himpunan
 *  `hargaTerlihat` yang DIKUMPULKAN sejak SKU baris ini dipilih. SKU berbeda =
 *  mulai dari nol: harga IMEI produk lama bukan harga otomatis produk baru. */
export function gabungInfoHarga(lama, baru, sku) {
  const sama = !!lama && lama.sku === sku;
  const terlihat = new Set(sama ? lama.hargaTerlihat || [] : []);
  if (baru.hargaProduk > 0) terlihat.add(baru.hargaProduk);
  for (const h of baru.hargaUnit) terlihat.add(h);
  return { ...baru, sku, hargaTerlihat: [...terlihat] };
}

/** Simpan respons pilihan-jual ke state pemilih IMEI: daftar saran DAN info
 *  harganya sekaligus. Fungsi modul (bukan closure komponen) supaya dipakai
 *  efek React tanpa menambah dependensi yang berubah tiap render. `setInfoHarga`
 *  dipanggil dengan fungsi pembaru — info MENGGABUNG respons, tidak menggantinya. */
export function terimaPilihanJual(data, setSaran, setInfoHarga, sku) {
  setSaran((data && data.items) || []);
  const baru = infoHargaDariRespons(data);
  setInfoHarga((lama) => gabungInfoHarga(lama, baru, sku));
}

/**
 * Patch harga saat kasir MEMILIH satu IMEI.
 * @returns {{patch: object, pesan: string|null, saran: number|null}}
 *   `patch` di-spread ke `onPatch` bersama `imei`; `pesan` untuk toast.
 */
export function hargaSaatPilihUnit({ item, unit, info, modeEdit = false }) {
  const kosong = { patch: {}, pesan: null, saran: null };
  // Nota yang SUDAH tercatat (edit Input Penjualan): harga tidak berubah
  // sendiri — koreksi nota lama punya aturan uangnya sendiri di server.
  if (modeEdit || !info || !info.dipakai || !unit) return kosong;
  // Baris Bonus/Gratis: harga 0 di sini PILIHAN kasir, bukan harga kosong.
  if (item && item.is_bonus) return kosong;
  const hargaUnit = bulat(unit.harga_jual_unit);
  const target = hargaUnit > 0 ? hargaUnit : bulat(info.hargaProduk);
  if (!(target > 0)) return kosong;
  const sekarang = bulat(item && item.harga);
  if (sekarang === target) return kosong;
  const otomatis = new Set([0, bulat(info.hargaProduk), ...(info.hargaUnit || []).map(bulat),
                            ...(info.hargaTerlihat || []).map(bulat)]);
  const label = hargaUnit > 0 ? "harga IMEI ini" : "harga produk (IMEI ini tanpa harga khusus)";
  if (otomatis.has(sekarang)) {
    // Sejak blok pembayaran N3, form kasir tidak punya "uang diterima" lagi.
    // Baris pembayaran utama ikut tagihan HANYA selama belum disentuh; pada
    // draf yang dibuka ulang ia tetap angka tersimpan (layar menampilkan
    // selisih + "Isi sisa"), dan Total Pembayaran yang sudah diketik kasir
    // tidak pernah ikut berubah — keduanya yang harus diperiksa ulang.
    return {
      patch: { harga: target },
      pesan: `Harga baris diisi ${label} ${rupiah(target)} — masih bisa diubah. `
        + "Tagihan ikut berubah: periksa lagi nominal baris pembayaran "
        + "(tombol \"Isi sisa\" bila ada selisih) dan Total Pembayaran bila sudah diisi.",
      saran: null,
    };
  }
  // Harga yang tidak dikenali belum tentu ketikan kasir (draf lama, unit yang
  // sudah tidak tersedia) — pesannya tidak menuduh, cukup menyebut kedua angka.
  return {
    patch: {},
    pesan: `Harga baris ${rupiah(sekarang)} tidak diubah karena bukan harga otomatis — `
      + `${label} ${rupiah(target)}. Ubah sendiri kalau perlu.`,
    saran: target,
  };
}

/** Ringkasan info unit untuk daftar pilihan IMEI ("BH 87% · Garansi s/d 12 Mar
 *  2027 · iBox · Rp 7.250.000") — supaya kasir/FL bisa membedakan HP second
 *  sejenis. Teks garansi dari SERVER (`garansi_teks`, satu penyusun dengan
 *  Price List & Excel), bukan dihitung ulang di sini. */
export function ringkasInfoUnit(u) {
  if (!u) return "";
  const bagian = [];
  if (u.battery_health != null && u.battery_health !== "") bagian.push(`BH ${u.battery_health}%`);
  if (u.garansi_teks) {
    bagian.push(`Garansi ${u.garansi_teks}${u.garansi_status === "habis" ? " (habis)" : ""}`);
  }
  if (bulat(u.harga_jual_unit) > 0) bagian.push(rupiah(u.harga_jual_unit));
  return bagian.join(" · ");
}
